import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Tabs } from 'antd';
import SignatureCanvas from 'react-signature-canvas';

import WrittenSignature, {
  WrittenSignatureElement,
} from 'v3/components/WrittenSignature';

import * as S from './styles';
import { current } from '@reduxjs/toolkit';

export type SignatureProps = {
  name: string;
  disabled?: boolean;
  setIsDrawingValid: (isValid: boolean) => void;
  description?: string | React.ReactNode;
};

export type SignatureElement = {
  getBase64Image: () => string;
};

export enum SignatureTabNames {
  AdoptSignature = 1,
  Draw = 2,
}

const Signature = forwardRef<SignatureElement, SignatureProps>(
  ({ name, setIsDrawingValid, disabled, description }, ref) => {
    const [sigPad, setSigPad] = useState<any>(null);
    const [activeTab, setActiveTab] = useState(1);

    const writtenSignatureRef = useRef<WrittenSignatureElement>(null);

    const clearButton = () => {
      sigPad.clear();
      setIsDrawingValid(false);
    };

    const getBase64Image = useCallback(() => {
      if (activeTab === SignatureTabNames.Draw) {
        return sigPad?.getTrimmedCanvas()?.toDataURL();
      }

      return writtenSignatureRef.current?.getBase64Image();
    }, [sigPad, activeTab]);

    const onTabChange = (currentTab: string) => {
      if (disabled) return;

      const currentTabNumber = Number(currentTab);

      console.log({currentTabNumber});

      if (currentTabNumber === SignatureTabNames.AdoptSignature) {
        setIsDrawingValid(true);
      } else if (!sigPad || sigPad.isEmpty()) {
        setIsDrawingValid(false);
      }

      setActiveTab(currentTabNumber);
    };

    useImperativeHandle(ref, () => ({
      getBase64Image,
    }));

    useEffect(() => {
      if (disabled && sigPad) {
        sigPad.off();
      }
    }, [disabled, sigPad]);

    return (
      <S.Wrapper>
        <Tabs
          onChange={onTabChange}
          activeKey={String(activeTab)}
          items={[
            {
              label: `Adopt Signature`,
              key: '1',
              children: (
                <S.SignatureWrapper>
                  <S.WrittenSignatureContainer>
                    <WrittenSignature
                      shouldRedraw={
                        activeTab === SignatureTabNames.AdoptSignature
                      }
                      ref={writtenSignatureRef}
                      name={name}
                      />
                  </S.WrittenSignatureContainer>
                </S.SignatureWrapper>
              ),
            },
            {
              label: `Draw`,
              key: '2',
              children: (
                <S.SignatureWrapper>
                  <SignatureCanvas
                    onEnd={() => setIsDrawingValid(true)}
                    canvasProps={{ className: 'signatureBoard' }}
                    ref={(ref) => setSigPad(ref)}
                  />
                  <S.ClearButton
                    type="button"
                    disabled={disabled}
                    onClick={clearButton}
                  >
                    Clear
                  </S.ClearButton>
                </S.SignatureWrapper>
              ),
            },
          ]}
        />
        {!!description && <S.Description>{description}</S.Description>}
      </S.Wrapper>
    );
  }
);

Signature.displayName = 'Signature';

export default Signature;
